import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchCurrentUserThunk } from '../../ducks/user.duck';
import { fetchStripeAccount } from '../../ducks/stripeConnectAccount.duck';
import { storableError } from '../../util/errors';
import { switchMarketplaceAccountUserType, fetchSwitchableAccountUserTypes } from '../../util/api';
import { countBlockingProviderSaleTransactions } from '../../util/providerSaleBlockingQueries';

function normalizedSwitchError(error) {
  if (
    error &&
    typeof error === 'object' &&
    (error.code !== undefined || error.counts !== undefined)
  ) {
    return error;
  }
  return storableError(error);
}

export const fetchAccountTypePreflightThunk = createAsyncThunk(
  'AccountTypePage/fetchPreflight',
  async (_, { extra: sdk, rejectWithValue }) => {
    try {
      const listingRes = await sdk.ownListings.query({
        states: 'published',
        page: 1,
        perPage: 1,
      });
      const publishedListings = Number(listingRes?.data?.meta?.totalItems ?? 0) || 0;
      const activeSales = await countBlockingProviderSaleTransactions(sdk);

      return { publishedListings, activeSales };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const fetchSwitchableUserTypesThunk = createAsyncThunk(
  'AccountTypePage/fetchSwitchableUserTypes',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchSwitchableAccountUserTypes();
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const submitSwitchUserTypeThunk = createAsyncThunk(
  'AccountTypePage/submitSwitchUserType',
  async ({ targetUserType }, { dispatch, getState, rejectWithValue }) => {
    try {
      await switchMarketplaceAccountUserType({ targetUserType });
      await dispatch(fetchCurrentUserThunk({ enforce: true })).unwrap();
      await dispatch(fetchSwitchableUserTypesThunk()).unwrap();
      const currentUser = getState().user?.currentUser;
      return { targetUserType, currentUser };
    } catch (e) {
      return rejectWithValue(normalizedSwitchError(e));
    }
  }
);

/** SSR + client loading: hydrate currentUser, switchable types, then downgrade preflight totals. */
export const loadData = () => (dispatch, getState) =>
  dispatch(fetchCurrentUserThunk({ enforce: true }))
    .then(() => {
      const currentUser = getState().user?.currentUser;
      if (currentUser && currentUser.stripeAccount) {
        dispatch(fetchStripeAccount());
      }
    })
    .then(() => dispatch(fetchSwitchableUserTypesThunk()))
    .then(() => dispatch(fetchAccountTypePreflightThunk()));

const slice = createSlice({
  name: 'AccountTypePage',
  initialState: {
    preflightListingCount: 0,
    preflightSaleCount: 0,
    preflightInProgress: false,
    preflightError: null,
    switchableTypes: [],
    lockedTypes: [],
    switchableCurrentUserType: null,
    hasStoredGroups: false,
    switchableTypesInProgress: false,
    switchableTypesError: null,
    submitError: null,
    submitInProgress: false,
  },
  reducers: {
    clearSubmitError: state => {
      state.submitError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAccountTypePreflightThunk.pending, state => {
        state.preflightInProgress = true;
        state.preflightError = null;
      })
      .addCase(fetchAccountTypePreflightThunk.fulfilled, (state, action) => {
        state.preflightListingCount = action.payload.publishedListings;
        state.preflightSaleCount = action.payload.activeSales;
        state.preflightInProgress = false;
      })
      .addCase(fetchAccountTypePreflightThunk.rejected, (state, action) => {
        state.preflightError = action.payload || null;
        state.preflightInProgress = false;
      })
      .addCase(fetchSwitchableUserTypesThunk.pending, state => {
        state.switchableTypesInProgress = true;
        state.switchableTypesError = null;
      })
      .addCase(fetchSwitchableUserTypesThunk.fulfilled, (state, action) => {
        state.switchableTypes = action.payload?.switchableTypes || [];
        state.lockedTypes = action.payload?.lockedTypes || [];
        state.switchableCurrentUserType = action.payload?.currentUserType || null;
        state.hasStoredGroups = !!action.payload?.hasStoredGroups;
        state.switchableTypesInProgress = false;
      })
      .addCase(fetchSwitchableUserTypesThunk.rejected, (state, action) => {
        state.switchableTypesError = action.payload || null;
        state.switchableTypesInProgress = false;
      })
      .addCase(submitSwitchUserTypeThunk.pending, state => {
        state.submitInProgress = true;
        state.submitError = null;
      })
      .addCase(submitSwitchUserTypeThunk.fulfilled, (state, action) => {
        state.submitInProgress = false;
        state.submitError = null;
        if (action.payload?.targetUserType) {
          state.switchableCurrentUserType = action.payload.targetUserType;
        }
      })
      .addCase(submitSwitchUserTypeThunk.rejected, (state, action) => {
        state.submitError = action.payload || null;
        state.submitInProgress = false;
      });
  },
});

export const { clearSubmitError } = slice.actions;

export default slice.reducer;
